import React, { useState } from 'react';
import { FaTrash, FaUpload } from 'react-icons/fa';
import * as clientCatalogService from '../../services/clientCatalog.service';
import * as documentService from '../../services/document.service';
import { TEAR_SHEET } from '../../constant';

export default function TearSheetDocumentsSection({ clientCode, catalogProduct, disabled, onCatalogUpdated }) {
  const compliance = catalogProduct?.productComplianceDocuments ?? [];
  const approved = catalogProduct?.approvedDocuments ?? [];
  const [busy, setBusy] = useState(null);
  const [docError, setDocError] = useState(null);
  const [docSuccess, setDocSuccess] = useState(null);

  const upload = async (category, inputEl) => {
    const raw = inputEl?.files;
    inputEl.value = '';
    if (!raw?.length || !clientCode || !catalogProduct?.uid) return;
    setBusy(category);
    setDocError(null);
    setDocSuccess(null);
    try {
      const data = await clientCatalogService.uploadTearSheetDocuments(
        clientCode,
        catalogProduct.uid,
        category,
        Array.from(raw),
      );
      if (data.catalog && typeof onCatalogUpdated === 'function') {
        onCatalogUpdated(data.catalog);
      }
      const uploaded = Array.isArray(data.uploaded) ? data.uploaded : [];
      const names = uploaded
        .map((u) => u.fileName || u.documentName)
        .filter(Boolean);
      setDocSuccess(
        names.length > 0
          ? `Upload successful — ${names.join(', ')}`
          : 'Upload successful.',
      );
    } catch (e) {
      setDocError(e.response?.data?.message || e.message || 'Upload failed');
      setDocSuccess(null);
    } finally {
      setBusy(null);
    }
  };

  const removeDoc = async (doc, apiCategory) => {
    const arrKey =
      apiCategory === 'product_compliance'
        ? 'productComplianceDocuments'
        : 'approvedDocuments';
    setBusy(`del-${doc.id}`);
    setDocError(null);
    setDocSuccess(null);
    try {
      await documentService.deleteDocument(doc.id);
      if (typeof onCatalogUpdated === 'function') {
        onCatalogUpdated({
          ...catalogProduct,
          [arrKey]: (catalogProduct[arrKey] || []).filter((d) => d.id !== doc.id),
        });
      }
    } catch (e) {
      setDocError(e.response?.data?.message || e.message || 'Delete failed');
    } finally {
      setBusy(null);
    }
  };

  const busyUpload = (cat) => busy === cat;
  const busyDel = (id) => busy === `del-${id}`;

  return (
    <section className="tear-sheet-section mt-3 tear-sheet-docs-section">
      <h6 className="tear-sheet-section-title">{TEAR_SHEET.SECTION_DOCS}</h6>
      <p className="small text-muted mb-2">{TEAR_SHEET.DOCS_HINT}</p>
      {docError && (
        <div className="alert alert-danger py-2 px-3 small mb-2" role="alert">
          {docError}
        </div>
      )}
      {docSuccess && (
        <div
          className="alert alert-success py-2 px-3 small mb-2 d-flex justify-content-between align-items-start gap-2"
          role="status"
        >
          <span>{docSuccess}</span>
          <button
            type="button"
            className="btn-close btn-close-sm ms-1"
            onClick={() => setDocSuccess(null)}
            aria-label="Dismiss upload message"
          />
        </div>
      )}
      <div className="row g-3">
        <div className="col-12 col-md-6">
          <div className="tear-sheet-doc-block border rounded p-2 h-100">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <span className="tear-sheet-lbl mb-0">Product Compliance</span>
              <label className="btn btn-sm btn-outline-primary mb-0">
                <FaUpload className="me-1" aria-hidden />
                Upload
                <input
                  type="file"
                  multiple
                  className="d-none"
                  disabled={disabled || !!busy}
                  aria-label="Upload product compliance documents"
                  onChange={(e) => upload('product_compliance', e.target)}
                />
              </label>
            </div>
            {busyUpload('product_compliance') && (
              <p className="small text-muted mb-1">Uploading…</p>
            )}
            <ul className="list-unstyled small tear-sheet-doc-list mb-0">
              {compliance.length === 0 ? (
                <li className="text-muted">No files yet.</li>
              ) : (
                compliance.map((d) => (
                  <li
                    key={d.id}
                    className="d-flex justify-content-between align-items-start gap-2 py-1 border-top tear-sheet-doc-row"
                  >
                    <span className="text-break">{d.documentName}</span>
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-danger p-0 flex-shrink-0"
                      disabled={disabled || !!busy}
                      aria-label={`Remove ${d.fileName || 'file'}`}
                      onClick={() => removeDoc(d, 'product_compliance')}
                    >
                      {busyDel(d.id) ? <span className="small">…</span> : <FaTrash aria-hidden />}
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
        <div className="col-12 col-md-6">
          <div className="tear-sheet-doc-block border rounded p-2 h-100">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <span className="tear-sheet-lbl mb-0">Approved documents</span>
              <label className="btn btn-sm btn-outline-primary mb-0">
                <FaUpload className="me-1" aria-hidden />
                Upload
                <input
                  type="file"
                  multiple
                  className="d-none"
                  disabled={disabled || !!busy}
                  aria-label="Upload approved documents"
                  onChange={(e) => upload('approved_document', e.target)}
                />
              </label>
            </div>
            {busyUpload('approved_document') && (
              <p className="small text-muted mb-1">Uploading…</p>
            )}
            <ul className="list-unstyled small tear-sheet-doc-list mb-0">
              {approved.length === 0 ? (
                <li className="text-muted">No files yet.</li>
              ) : (
                approved.map((d) => (
                  <li
                    key={d.id}
                    className="d-flex justify-content-between align-items-start gap-2 py-1 border-top tear-sheet-doc-row"
                  >
                    <span className="text-break">{d.documentName}</span>
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-danger p-0 flex-shrink-0"
                      disabled={disabled || !!busy}
                      aria-label={`Remove ${d.fileName || 'file'}`}
                      onClick={() => removeDoc(d, 'approved_document')}
                    >
                      {busyDel(d.id) ? <span className="small">…</span> : <FaTrash aria-hidden />}
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
